import { Suspense } from 'react';

import { AppLoader } from '@/shared/components/feedback/AppLoader';
import { createBrowserRouter, Outlet, RouterProvider } from 'react-router-dom';

import { AppLayout } from '@/app/layouts/AppLayout';
import { AuthLayout } from '@/app/layouts/AuthLayout';
import { ROUTES } from '@/config/routes';
import { authRoutes, fullPageAuthRoutes } from '@/features/auth';
import { brandAnalyserRoutes } from '@/features/brandAnalyser';
import { agentsOverviewRoutes } from '@/features/agentsOverview';
import { brandOverviewRoutes } from '@/features/brandOverview';
import { collectionsRoutes } from '@/features/collections';
import { dashboardRoutes } from '@/features/dashboard';
import { emailWarmupPlatformRoutes, emailWarmupRoutes } from '@/features/emailWarmup';
import { linkedinAgentRoutes } from '@/features/linkedinAgent';
import { accountRoutes } from '@/features/account';
import { notificationRoutes } from '@/features/notifications';
import { moduleBillingRoutes } from '@/features/moduleBilling';
import { developerRoutes } from '@/features/developer';
import { platformBillingRoutes } from '@/features/platformBilling';
import { placeholderRoutes } from '@/features/placeholder';
import { reviewsPlatformRoutes, reviewsRoutes } from '@/features/reviews';
import { seoAgentRoutes } from '@/features/seoAgent';
import { assistantRoutes } from '@/features/assistant';
import { supportPlatformRoutes, supportRoutes } from '@/features/support';
import { teamRoutes } from '@/features/team';

import { ProductRoute } from './ProductRoute';
import { ProtectedRoute } from './ProtectedRoute';
import { SuperAdminRoute } from './SuperAdminRoute';
import { RootRedirect } from './RootRedirect';
import { RouteAnnouncer } from './RouteAnnouncer';

/**
 * The route table.
 *
 * <h2>Three groups, not one list</h2>
 *
 * Public pages, authentication pages and the signed-in application differ in
 * their layout and in whether they need a session. Nesting them under a layout
 * each states that once, instead of every route repeating a guard — and a route
 * added later inherits the right treatment by where it is put rather than by
 * somebody remembering.
 *
 * <p>Feature routes are added by importing the feature's own route array, so this
 * file lists the shape of the application and never grows a screen's worth of
 * detail.
 */
const router = createBrowserRouter([
  {
    // No layout: these compose their own page.
    element: (
      <>
        <RouteAnnouncer />
        {/*
         * The one loader, so a cold start is continuous.

         * index.html paints it before React exists. If this boundary fell back to
         * null, React's first commit would replace that with nothing and a refresh
         * would read loader, blank, content -- reintroducing the flicker from the
         * other side, having just removed it. The same component here means the
         * handover changes who owns the pixels and not what is drawn.
         */}
        <Suspense fallback={<AppLoader />}>
          <Outlet />
        </Suspense>
      </>
    ),
    children: fullPageAuthRoutes,
  },
  {
    element: (
      <>
        <RouteAnnouncer />
        <AuthLayout />
      </>
    ),
    children: authRoutes,
  },
  {
    element: (
      <>
        <RouteAnnouncer />
        <ProtectedRoute />
      </>
    ),
    children: [
      {
        element: <AppLayout />,
        children: [
          ...dashboardRoutes,
          ...teamRoutes,
          ...supportRoutes,
          ...assistantRoutes,
          ...moduleBillingRoutes,
          ...accountRoutes,
          ...notificationRoutes,

          /*
           * Everything else in the shell, behind the product guard.
           *
           * <p>Nested rather than listed beside the others for the same reason the
           * platform group below is: a screen added to the group is guarded by where it
           * is put, not by somebody remembering. A path that is not a menu row -- the
           * assistant, a collection, the account screens -- names no product and passes
           * straight through, so putting them all behind it costs nothing and means the
           * next product screen is covered without an edit here.
           *
           * <p>It is a courtesy, not the boundary. The server answers 403
           * `product-not-assigned` on every product endpoint; this says WHY rather than
           * letting each screen surface its own failure.
           */
          {
            element: <ProductRoute />,
            children: [
              // Before the placeholders, which still generate an entry for any screen not
              // yet in their `BUILT_ROUTES`. Inside this group, so the product guard
              // applies by placement rather than by somebody remembering.
              ...emailWarmupRoutes,
              ...brandAnalyserRoutes,
              ...brandOverviewRoutes,
              ...collectionsRoutes,
              ...reviewsRoutes,
              ...developerRoutes,
              ...agentsOverviewRoutes,
              ...seoAgentRoutes,
              ...linkedinAgentRoutes,
              ...placeholderRoutes,
            ],
          },
          /*
           * The platform operator's own screens, inside the same shell and behind a
           * second guard.
           *
           * <p>Nested rather than listed beside the others so the guard applies to the
           * group: a screen added to one of these arrays is protected by where it is put,
           * not by somebody remembering. ProtectedRoute above has already established
           * there is a session; this one waits for /account/me and checks the role.
           *
           * <p>Inside AppLayout deliberately. The operator is a signed-in person with a
           * menu and an account button like anybody else — a separate chrome would be a
           * second application to keep in step for one extra screen.
           */
          {
            element: <SuperAdminRoute />,
            children: [
              ...emailWarmupPlatformRoutes,
              ...reviewsPlatformRoutes,
              ...supportPlatformRoutes,
              ...platformBillingRoutes,
            ],
          },
        ],
      },
    ],
  },
  {
    /*
     * `/` redirects rather than rendering anything.
     *
     * It served the marketing landing page, which was the single biggest thing in
     * the bundle -- the page, the navigation, the mega dropdown, the footer and the
     * trusted-by section, all in the main chunk, downloaded and parsed before ANY
     * route could render. Somebody opening /signup waited for a site they had not
     * asked for; the sign-up screen's own code is 10 kB.
     *
     * The marketing site is xtremand.com. This application starts at the sign-in
     * page, and at the dashboard for anybody who already has a session.
     */
    path: ROUTES.home,
    element: (
      <>
        <RouteAnnouncer />
        <RootRedirect />
      </>
    ),
  },
  {
    path: ROUTES.notFound,
    element: (
      <div className="flex min-h-full items-center justify-center p-6">
        <p className="text-[var(--color-ink-soft)]">That page does not exist.</p>
      </div>
    ),
  },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
